import { useState } from "react"
import { Head, Link, router } from "@inertiajs/react"
import { AdminShell } from "@/components/AdminShell"
import { Button } from "@/components/ui/button"
import { FlaskConical } from "lucide-react"

type PersonRow = {
  id: number
  name: string
  slug: string
  birth_year: number | null
  death_year: number | null
  death_place: string | null
  all_recordings_added: boolean
  in_amh: boolean
}

type Filters = {
  name?: string
  birth_year?: string
  death_year?: string
  death_place?: string
  all_recordings_added?: string
  in_amh?: string
}

type Props = {
  people: PersonRow[]
  total_count: number
  capped: boolean
  filters: Filters
  sort: string
  dir: "asc" | "desc"
}

const ENTITY_TABS = [
  { label: "Recordings",      href: "/admin/research/recordings" },
  { label: "People",          href: "/admin/research/people" },
  { label: "Gigs",            href: "/admin/research/gigs" },
  { label: "Chart Listings",  href: "/admin/research/chart_listings" },
]

const ic = "w-full rounded border border-hairline bg-page px-3 py-1.5 text-sm text-ink-body placeholder:text-ink-muted focus:outline-none focus:ring-1 focus:ring-current"

export default function ResearchPeople({ people, total_count, capped, filters, sort, dir }: Props) {
  const [form, setForm] = useState<Filters>(filters)

  function handleChange(e: React.ChangeEvent<HTMLInputElement>) {
    const { name, type, checked, value } = e.target
    setForm((prev) => ({ ...prev, [name]: type === "checkbox" ? (checked ? "1" : "") : value }))
  }

  function buildAllParams(overrides: Record<string, string> = {}): Record<string, string> {
    const p: Record<string, string> = {}
    Object.entries(form).forEach(([k, v]) => { if (v) p[k] = v })
    if (sort && sort !== "name") p.sort = sort
    if (dir && dir !== "asc") p.dir = dir
    return { ...p, ...overrides }
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    router.get("/admin/research/people", buildAllParams(), { preserveState: false })
  }

  function handleReset() {
    setForm({})
    router.get("/admin/research/people", {}, { preserveState: false })
  }

  function handleSort(col: string) {
    const newDir = sort === col && dir === "asc" ? "desc" : "asc"
    router.get("/admin/research/people", buildAllParams({ sort: col, dir: newDir }), { preserveState: false })
  }

  function SortHeader({ col, children }: { col: string; children: React.ReactNode }) {
    const active = sort === col
    return (
      <th className="pb-2 pr-4 font-medium">
        <button onClick={() => handleSort(col)} className="flex items-center gap-1 hover:text-ink-body transition-colors whitespace-nowrap">
          {children}
          <span className={active ? "text-ink-body" : "opacity-30"}>
            {active ? (dir === "asc" ? "↑" : "↓") : "↕"}
          </span>
        </button>
      </th>
    )
  }

  const isFiltering = Object.values(filters).some(Boolean)

  return (
    <>
      <Head title="Research: People">
        <meta name="description" content="Filter and search people by field." />
        <meta property="og:title" content="Research: People" />
        <meta property="og:description" content="Filter and search people by field." />
      </Head>
      <AdminShell>
        <div className="border-b border-hairline pb-6">
          <div className="flex items-center gap-2 mb-1">
            <FlaskConical className="h-5 w-5 text-ink-muted" />
            <h1>Research</h1>
          </div>
          <div className="flex gap-4 mt-3 border-b border-hairline -mx-6 sm:-mx-10 px-6 sm:px-10">
            {ENTITY_TABS.map((tab) => (
              <Link
                key={tab.href}
                href={tab.href}
                className={[
                  "pb-3 text-sm font-medium no-underline border-b-2 -mb-px transition-colors",
                  tab.label === "People"
                    ? "border-current text-ink-body"
                    : "border-transparent text-ink-muted hover:text-ink-body"
                ].join(" ")}
              >
                {tab.label}
              </Link>
            ))}
          </div>
        </div>

        <form onSubmit={handleSubmit} className="mt-6 grid grid-cols-2 gap-3 sm:grid-cols-3">
          <div className="col-span-2 sm:col-span-3">
            <label className="block text-xs font-medium text-ink-muted mb-1">Name</label>
            <input name="name" type="text" value={form.name ?? ""} onChange={handleChange} placeholder="e.g. Jimmy Barnes" className={ic} />
          </div>
          <div>
            <label className="block text-xs font-medium text-ink-muted mb-1">Birth year</label>
            <input name="birth_year" type="number" value={form.birth_year ?? ""} onChange={handleChange} placeholder="e.g. 1956" className={ic} min={1800} max={2100} />
          </div>
          <div>
            <label className="block text-xs font-medium text-ink-muted mb-1">Death year</label>
            <input name="death_year" type="number" value={form.death_year ?? ""} onChange={handleChange} placeholder="e.g. 2020" className={ic} min={1800} max={2100} />
          </div>
          <div>
            <label className="block text-xs font-medium text-ink-muted mb-1">Death place</label>
            <input name="death_place" type="text" value={form.death_place ?? ""} onChange={handleChange} placeholder="e.g. Sydney" className={ic} />
          </div>
          <div className="col-span-2 sm:col-span-3 flex flex-wrap gap-4 pt-1">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                name="all_recordings_added"
                type="checkbox"
                checked={form.all_recordings_added === "1"}
                onChange={handleChange}
                className="h-4 w-4 rounded border-hairline accent-current"
              />
              <span className="text-sm text-ink-muted">All recordings added</span>
            </label>
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                name="in_amh"
                type="checkbox"
                checked={form.in_amh === "1"}
                onChange={handleChange}
                className="h-4 w-4 rounded border-hairline accent-current"
              />
              <span className="text-sm text-ink-muted">Featured in AMH</span>
            </label>
          </div>
          <div className="col-span-2 sm:col-span-3 flex items-center gap-3 pt-1">
            <Button type="submit">Search</Button>
            {isFiltering && (
              <button type="button" onClick={handleReset} className="text-sm text-ink-muted hover:text-ink-body transition-colors">
                Clear filters
              </button>
            )}
          </div>
        </form>

        {isFiltering && (
          <p className="mt-4 text-sm text-ink-muted">
            {capped
              ? `Showing first 200 of ${total_count} results`
              : `${total_count} ${total_count === 1 ? "result" : "results"}`}
          </p>
        )}

        {isFiltering && people.length === 0 ? (
          <p className="mt-8 text-center text-sm text-ink-muted">No people match your filters.</p>
        ) : people.length > 0 ? (
          <div className="mt-6 overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-hairline text-left text-xs text-ink-muted">
                  <SortHeader col="name">Name</SortHeader>
                  <SortHeader col="birth_year">Born</SortHeader>
                  <SortHeader col="death_year">Died</SortHeader>
                  <SortHeader col="death_place">Death place</SortHeader>
                  <th className="pb-2 pr-4 font-medium">AMH</th>
                  <th className="pb-2 font-medium">All recs</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-hairline">
                {people.map((p) => (
                  <tr key={p.id} className="hover:bg-surface">
                    <td className="py-2 pr-4">
                      <Link href={`/people/${p.slug}`} className="font-medium text-ink-body hover:text-amber-600 dark:hover:text-amber-400 no-underline">
                        {p.name}
                      </Link>
                    </td>
                    <td className="py-2 pr-4 text-ink-muted tabular-nums">{p.birth_year ?? "—"}</td>
                    <td className="py-2 pr-4 text-ink-muted tabular-nums">{p.death_year ?? "—"}</td>
                    <td className="py-2 pr-4 text-ink-muted">{p.death_place ?? "—"}</td>
                    <td className="py-2 pr-4 text-ink-muted">{p.in_amh ? "✓" : ""}</td>
                    <td className="py-2 text-ink-muted">{p.all_recordings_added ? "✓" : ""}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : null}
      </AdminShell>
    </>
  )
}
